/**
 * @module {raf} util/raf
 * @parent util.modules
 *
 * Provides a ponyfill for the `window.requestAnimationFrame` DOM API and utility
 * methods for ease of use.
 *
 * @option {raf}
 * A ponyfill &mdash; A safe polyfill which doesn’t modify global objects. &mdash; for
 * the `window.requestAnimationFrame` DOM API.
 *
 * This module papers over the vendor-prefixed variations of the DOM API and internally
 * falls back on an emulated implementation using basic `window.setTimeout` API for
 * browsers that have no native support at all.
 *
 * In addition, there are some shorthand method names provided for better readability
 * and increased capacity for minification, as well as utility methods that assist in
 * some common usage scenarios.
 *
 * The emulated implementation is an extension and adaptation of the colloquial
 * [jquery.requestAnimationFrame](https://github.com/gnarf37/jquery-requestAnimationFrame)
 * plugin.
 */
define('util/raf',[], function() {

	"use strict";

	var
		win                   = window,
		vendors               = [ "webkit", "moz" ],
		requestAnimationFrame = win.requestAnimationFrame,
		cancelAnimationFrame  = win.cancelAnimationFrame,
		lastTime,
		isNative,
		n;

	for( n = vendors.length - 1; -1 !== n && !requestAnimationFrame; --n ) {
		requestAnimationFrame = win[ vendors[ n ] + "RequestAnimationFrame" ];
		cancelAnimationFrame  = cancelAnimationFrame
			|| win[ vendors[ n ] + "CancelAnimationFrame" ]
			|| win[ vendors[ n ] + "CancelRequestAnimationFrame" ];
	}

	if ( !( isNative = requestAnimationFrame != null )) {
		requestAnimationFrame = function( callback ) {
			var
				currentTime = new Date().getTime(),
				timeoutTime = !isNaN( lastTime ) ? Math.max( 0, 16 - ( currentTime - lastTime )) : 0
				reportTime  = lastTime = currentTime + timeoutTime;

			lastTime = reportTime;

			return win.setTimeout( function() {
				callback.call( win, reportTime );
			}, timeoutTime );
		};
		
		cancelAnimationFrame = function( id ) {
			clearTimeout( id );
		};
	} else {
		requestAnimationFrame = function() {
			return win.requestAnimationFrame.apply( win, arguments );
		}
		cancelAnimationFrame = function() {
			return win.cancelAnimationFrame.apply( win, arguments );
		}
	}

	return {
		/**
		 * @property {Boolean}
		 * @parent util/raf 1
		 * Indicates whether the native `window.requestAnimationFrame` DOM API is being
		 * used.
		 *
		 * This property can be used to implement branching logic in cases where animation
		 * based on an emulated API is non-desirable and an alternative is required.
		 */
		isNative : isNative,

		/**
		 * Tells the browser that you wish to perform an animation and requests that
		 * the browser call a specified function to update an animation before the next
		 * repaint.
		 *
		 * @signature `rAF.requestAnimationFrame(callback)`
		 *
		 * @param {function(Number)} callback(timestamp)
		 * A function to call when it's time to update your animation for the next repaint.
		 * 
		 * @option timestamp
		 * Indicates the current time for when the browser starts executing the callback.
		 * This timestamp is a decimal number, in milliseconds.
		 *
		 * @return {Number}
		 * A long integer value that uniquely identifies the callback request. This will be a non-zero
		 * value. No further assumptions about its value are possible. This value may be passed to
		 * [util/raf.cancel cancel] or [util/raf.cancelAnimationFrame cancelAnimationFrame] to cancel
		 * the callback request.
		 *
		 * @body
		 * @codestart
		 * var
		 *   start   = null,
		 *   element = document.getElementById( "element-to-animate" );
		 *
		 * function step(timestamp) {
		 *   var progress;
		 *
		 *   if ( start == null ) { start = timestamp; }
		 *
		 *   progress = timestamp - start;
		 *   element.style.left = Math.min( progress / 10, 200 ) + "px";
		 *
		 *   if ( progress < 2000 ) { rAF.requestAnimationFrame( step ); }
		 * }
		 *
		 * rAF.requestAnimationFrame( step );
		 * @codeend
		 *
		 * @link https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame MDN
		 * @link http://caniuse.com/#feat=requestanimationframe CanIUse
		 */
		requestAnimationFrame : requestAnimationFrame,

		/**
		 * Cancels an animation frame request previously scheduled through a call to
		 * [util/raf.request request] or [util/raf.requestAnimationFrame requestAnimationFrame].
		 *
		 * @signature `rAF.cancelAnimationFrame(requestId)`
		 *
		 * @param {Number} requestId
		 * The ID value returned by the call to [util/raf.request request] or
		 * [util/raf.requestAnimationFrame requestAnimationFrame] that requested the callback.
		 *
		 * @link https://developer.mozilla.org/en-US/docs/Web/API/window/cancelAnimationFrame MDN
		 * @link http://caniuse.com/#feat=requestanimationframe CanIUse
		 */
		cancelAnimationFrame : cancelAnimationFrame,

		/**
		 * Tells the browser that you wish to perform an animation and requests that
		 * the browser call a specified function to update an animation before the next
		 * repaint.
		 *
		 * @signature `rAF.request(callback)`
		 *
		 * @param {function(Number)} callback(timestamp)
		 * A function to call when it's time to update your animation for the next repaint.
		 * 
		 * @option timestamp
		 * Indicates the current time for when the browser starts executing the callback.
		 * This timestamp is a decimal number, in milliseconds.
		 *
		 * @return {Number}
		 * A long integer value that uniquely identifies the callback request. This will be a non-zero
		 * value. No further assumptions about its value are possible. This value may be passed to
		 * [util/raf.cancel cancel] or [util/raf.cancelAnimationFrame cancelAnimationFrame] to cancel
		 * the callback request.
		 *
		 * @body
		 * @codestart
		 * var
		 *   start   = null,
		 *   element = document.getElementById( "element-to-animate" );
		 *
		 * function step(timestamp) {
		 *   var progress;
		 *
		 *   if ( start == null ) { start = timestamp; }
		 *
		 *   progress = timestamp - start;
		 *   element.style.left = Math.min( progress / 10, 200 ) + "px";
		 *
		 *   if ( progress < 2000 ) { rAF.request( step ); }
		 * }
		 *
		 * rAF.request( step );
		 * @codeend
		 *
		 * @link https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame MDN
		 * @link http://caniuse.com/#feat=requestanimationframe CanIUse
		 */
		request : requestAnimationFrame,

		
		/**
		 * Cancels an animation frame request previously scheduled through a call to
		 * [util/raf.request request] or [util/raf.requestAnimationFrame requestAnimationFrame].
		 *
		 * @signature `rAF.cancel(requestId)`
		 *
		 * @param {Number} requestId
		 * The ID value returned by the call to [util/raf.request request] or
		 * [util/raf.requestAnimationFrame requestAnimationFrame] that requested the callback.
		 *
		 * @link https://developer.mozilla.org/en-US/docs/Web/API/window/cancelAnimationFrame MDN
		 * @link http://caniuse.com/#feat=requestanimationframe CanIUse
		 */
		cancel : cancelAnimationFrame,

		/**
		 * Defers execution of a function to the next available animation frame and binds it
		 * to its context. Multiple calls are coalesced into one single call of the deferred
		 * function on the following animation frame using the last passed in arguments.
		 *
		 * @signature `rAF.defer(fn [, context])`
		 * 
		 * @param {function}
		 * The function to wrap.
		 * @param {{}} [context]
		 * The context to bind the function to. When no context is passed, the callee's own
		 * context is retained.
		 * @return {function}
		 * A function that calls __fn__ in the context of __context__ at the next available
		 * animation frame. Multiple calls are coalesced into a single call using the last
		 * passed in arguments.
		 */
		defer : function( fn, context ) {
			var
				args      = null,
				scheduled = null;

			return function() {
				var i, n;

				// NOTE: Explicit copy of arguments avoids optimizer bailout in V8.
				args = new Array( arguments.length );
				for ( i = 0, n = arguments.length; i !== n; ++i ) {
					args[ i ] = arguments[ i ];
				}

				if ( scheduled === null ) {
					scheduled = requestAnimationFrame( function() {
						var temp = args;

						// Blank these out before the call to `fn`, so that recursive
						// calls work as expected.
						args
							= scheduled
							= null;

						fn.apply( context || this, temp );
					});
				}
			}
		}
	};
});
